import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { QRAnalyticsService } from '../../../services/qrAnalyticsService';

const RealTimeScanFeed = ({ 
  scans = [],
  isLoading = false,
  isPaused = false,
  onTogglePause
}) => {
  const [localScans, setLocalScans] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [error, setError] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Load real-time scan data
  useEffect(() => {
    const loadRecentScans = async () => {
      setLocalLoading(true);
      setError('');

      try {
        const { data: scanData, error: scanError } = await QRAnalyticsService?.getRecentScans(50);
        
        if (scanError) {
          setError(scanError);
          setLocalScans([]); // No fallback data
        } else {
          setLocalScans(scanData || []);
        }
      } catch (error) {
        setError(`Failed to load recent scans: ${error?.message}`);
        setLocalScans([]); // No fallback data
      } finally {
        setLocalLoading(false);
      }
    };

    loadRecentScans();
  }, []);

  // Auto-refresh functionality
  useEffect(() => {
    if (!autoRefresh || isPaused) return;

    const interval = setInterval(async () => {
      // Only refresh if we're not using prop data
      if (!scans || scans?.length === 0) {
        try {
          const { data: scansData } = await QRAnalyticsService?.getRecentScans(20);
          if (scansData && scansData?.length > 0) {
            setLocalScans(scansData);
          }
        } catch (error) {
          console.log('Auto-refresh failed:', error);
        }
      }
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, isPaused, scans]);

  const showLoading = isLoading || localLoading;
  const displayScans = scans?.length > 0 ? scans : localScans || [];

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const scanTime = new Date(timestamp);
    const diffMs = now?.getTime() - scanTime?.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    if (diffMins > 0) return `${diffMins}m ago`;
    return 'Just now';
  };

  const getDeviceIcon = (device) => {
    const deviceLower = (device || '')?.toLowerCase();
    if (deviceLower?.includes('iphone') || deviceLower?.includes('ios')) return 'Smartphone';
    if (deviceLower?.includes('android')) return 'Smartphone';
    if (deviceLower?.includes('ipad') || deviceLower?.includes('tablet')) return 'Tablet';
    if (deviceLower?.includes('desktop') || deviceLower?.includes('windows') || deviceLower?.includes('mac')) return 'Monitor';
    return 'Smartphone';
  };

  const handleTogglePause = () => {
    if (onTogglePause) {
      onTogglePause();
    } else {
      setAutoRefresh(!autoRefresh);
    }
  };

  if (showLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Real-time Scan Feed</h3>
          <div className="animate-spin">
            <Icon name="Loader2" size={20} className="text-gray-400" />
          </div>
        </div>
        <div className="space-y-4">
          {[...Array(5)]?.map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="flex items-center space-x-3 py-3">
                <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-24"></div>
                </div>
                <div className="h-3 bg-gray-200 rounded w-16"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Enhanced scan display with more technical details
  const renderScanDetails = (scan) => (
    <div className="flex items-center space-x-3 py-3 px-3 rounded-lg hover:bg-gray-50 transition-colors group">
      {/* Device Icon */}
      <div className="flex-shrink-0">
        <div className="w-8 h-8 bg-blue-50 rounded-full flex items-center justify-center">
          <Icon 
            name={getDeviceIcon(scan?.device)} 
            size={16} 
            className="text-blue-600" 
          />
        </div>
      </div>

      {/* Scan Details */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center space-x-2">
          <h4 className="font-medium text-gray-900 truncate">
            {scan?.qrName || 'Unknown QR Code'}
          </h4>
          
          {/* Status Badges */}
          <div className="flex items-center space-x-1">
            {scan?.isNewUser && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                New
              </span>
            )}
            {scan?.converted && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                ✓ Converted
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-2 mt-1">
          <span className="text-sm text-gray-500">
            {scan?.device} • {scan?.location}
          </span>
          
          {/* Enhanced technical info - visible on hover */}
          <div className="hidden group-hover:block text-xs text-gray-400">
            {scan?.browser && `• ${scan?.browser}`}
            {scan?.os && ` • ${scan?.os}`}
            {scan?.utmSource && ` • UTM: ${scan?.utmSource}`}
          </div>
        </div>

        {/* Additional details for detailed view */}
        {scan?.showDetails && (
          <div className="mt-2 text-xs text-gray-500 space-y-1">
            {scan?.userAgent && (
              <div>
                <span className="font-medium">User Agent:</span> {scan?.userAgent?.substring(0, 80)}...
              </div>
            )}
            {scan?.ipAddress && (
              <div>
                <span className="font-medium">IP:</span> {scan?.ipAddress}
              </div>
            )}
            {scan?.referrer && (
              <div>
                <span className="font-medium">Referrer:</span> {scan?.referrer}
              </div>
            )}
            {(scan?.utmSource || scan?.utmMedium || scan?.utmCampaign) && (
              <div>
                <span className="font-medium">UTM:</span>
                {scan?.utmSource && ` Source: ${scan?.utmSource}`}
                {scan?.utmMedium && ` Medium: ${scan?.utmMedium}`}
                {scan?.utmCampaign && ` Campaign: ${scan?.utmCampaign}`}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Timestamp and expand button */}
      <div className="flex-shrink-0 text-right">
        <div className="text-sm text-gray-500">
          {formatTimeAgo(scan?.timestamp)}
        </div>
        <button
          onClick={() => {
            // Toggle details for this scan
            scan.showDetails = !scan?.showDetails;
            setLocalScans([...localScans]);
          }}
          className="mt-1 text-xs text-gray-400 hover:text-gray-600"
        >
          {scan?.showDetails ? 'Less' : 'More'}
        </button>
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <h3 className="text-lg font-semibold text-gray-900">Real-time Scan Feed</h3>
          {!isPaused && autoRefresh && (
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-xs text-green-600 font-medium">Live</span>
            </div>
          )}
          {(isPaused || !autoRefresh) && (
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
              <span className="text-xs text-gray-500 font-medium">Paused</span>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleTogglePause}
            className={`${
              (isPaused || !autoRefresh) 
                ? 'text-green-600 hover:text-green-700 hover:bg-green-50' :'text-gray-600 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Icon 
              name={(isPaused || !autoRefresh) ? 'Play' : 'Pause'} 
              size={16} 
            />
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.location?.reload()}
            className="text-gray-600 hover:text-gray-700 hover:bg-gray-50"
          >
            <Icon name="RefreshCw" size={16} />
          </Button>
        </div>
      </div>
      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="flex items-start">
            <Icon name="AlertTriangle" size={16} className="text-yellow-600 mt-0.5 mr-2" />
            <div>
              <p className="text-sm text-yellow-800 font-medium">Data Loading Issue</p>
              <p className="text-xs text-yellow-700 mt-1">{error} - Showing preview data</p>
            </div>
          </div>
        </div>
      )}
      {/* Scan Feed with enhanced details */}
      <div className="space-y-0 max-h-96 overflow-y-auto">
        {displayScans?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Activity" size={48} className="text-gray-300 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-600 mb-2">No Recent Activity</h4>
            <p className="text-gray-500">
              Scan activity with technical environment, marketing attribution, and user behavior data will appear here.
            </p>
          </div>
        ) : (
          displayScans?.map((scan, index) => (
            <div key={scan?.id || index}>
              {renderScanDetails(scan)}
            </div>
          ))
        )}
      </div>

      {/* Footer with enhanced info */}
      {displayScans?.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between text-sm text-gray-500">
            <span>Showing last {displayScans?.length} scans with full tracking data</span>
            <div className="flex items-center space-x-4">
              <span>Updates every 30 seconds</span>
              <span className="text-xs bg-gray-100 px-2 py-1 rounded">Click "More" for technical details</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RealTimeScanFeed;